import axios, { AxiosError } from 'axios';

import { FORBIDDEN_ERROR } from '@/assets/AppConstants';
import { wasSentWithPreviousWorkspaceScope } from '@/interceptors/workspaceScopeHeaders';

/** Ways a request gets no answer that another try may fix: the network failed or the wait ran out. */
const UNANSWERED_CODES: ReadonlySet<string> = new Set([
  AxiosError.ERR_NETWORK, AxiosError.ECONNABORTED, AxiosError.ETIMEDOUT,
]);

/** Answers that ask for another try: the server or a proxy was slow, busy or restarting. */
const TRANSIENT_STATUSES: ReadonlySet<number> = new Set([408, 429, 502, 503, 504]);

export const MAX_RETRIES = 3;

/**
 * Whether sending a failed query again may change its outcome.
 *
 * Every query goes out through an axios client, so an error that is not an AxiosError was thrown by
 * our own code and would be thrown again. A request that got no answer may get one. An answer is the
 * server's final word on that request, except when it asked for another try or when a 403 judged a
 * workspace scope the session has since left: the retry goes out with the current one.
 */
export const isTransientError = (error: unknown): boolean => {
  if (!axios.isAxiosError(error)) return false;
  if (error.response === undefined) return UNANSWERED_CODES.has(error.code ?? '');
  const { status } = error.response;
  if (status === FORBIDDEN_ERROR) return wasSentWithPreviousWorkspaceScope(error);
  return TRANSIENT_STATUSES.has(status);
};

/** A query's `retry`: at most `maxRetries` more attempts, and only while the failure is transient. */
export const retryTransient = (maxRetries: number) => (failureCount: number, error: unknown): boolean =>
  failureCount < maxRetries && isTransientError(error);
